
import React, { useState } from 'react';
import type { StaffMember, Department, View, DayOfWeek } from '../../types';
import StaffModal from './StaffModal';
import StaffProfileModal from './StaffProfileModal';
import { Icons } from '../ui/Icons';
import ConfirmationModal from '../ui/ConfirmationModal';
import { useDeleteHandler } from '../../hooks/useDeleteHandler';

interface StaffManagementProps {
    staffList: StaffMember[];
    departments: Department[];
    weekStartDay: DayOfWeek;
    setView: (view: View) => void;
    onSaveStaff: (staff: StaffMember) => void;
    onDeleteStaff: (staffId: string) => void;
}

const getInitials = (name: string) => {
    const names = name.split(' ');
    if (names.length > 1 && names[names.length - 1]) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};


const StaffManagement: React.FC<StaffManagementProps> = ({ staffList, departments, weekStartDay, setView, onSaveStaff, onDeleteStaff }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
    const [viewingStaff, setViewingStaff] = useState<StaffMember | null>(null);
    
    const {
        itemToDelete: staffToDelete,
        isDeleting,
        initiateDelete: handleDeleteClick,
        confirmDelete: handleConfirmDelete,
        cancelDelete: handleCancelDelete,
    } = useDeleteHandler<StaffMember>({
        onDeleteAsync: async (staff) => onDeleteStaff(staff.id),
        entityName: 'Staff Member',
        getDisplayName: (staff) => staff.name,
        getItemId: (staff) => staff.id,
    });


    const handleAddStaff = () => {
        setEditingStaff(null);
        setIsModalOpen(true);
    };

    const handleEditStaff = (staff: StaffMember) => {
        setEditingStaff(staff);
        setIsModalOpen(true);
    };
    
    const handleViewProfile = (staff: StaffMember) => {
        setViewingStaff(staff);
    };

    const handleSaveStaff = (staff: StaffMember) => {
        onSaveStaff(staff);
        setIsModalOpen(false);
    };

    const departmentMap = new Map<string, Department>(departments.map(d => [d.id, d]));
    const activeStaffList = staffList.filter(s => s.status !== 'deleted');

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Staff Roster</h2>
                <button
                    onClick={handleAddStaff}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors shadow-sm"
                >
                    <Icons.UserPlus className="w-5 h-5" />
                    Add Staff
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                 {activeStaffList.map(staff => {
                    const department = staff.departmentId ? departmentMap.get(staff.departmentId) : undefined;
                    return (
                        <div key={staff.id} className="bg-white dark:bg-slate-800 rounded-lg shadow-md overflow-hidden transition-shadow hover:shadow-lg flex flex-col">
                            <div className="p-5 flex-grow">
                                <div className="flex items-center gap-4">
                                    <div
                                        className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-lg"
                                        style={{ backgroundColor: staff.color }}
                                    >
                                        {getInitials(staff.name)}
                                    </div>
                                    <div className="flex-grow">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{staff.name}</h3>
                                            <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${
                                                staff.status === 'inactive' 
                                                    ? 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-400 dark:border-rose-900/40' 
                                                    : 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-900/40'
                                            }`}>
                                                {staff.status === 'inactive' ? 'Inactive' : 'Active'}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">{staff.role}</p>
                                    </div>
                                </div>
                                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                                    {department && (
                                        <div className="flex items-center gap-2">
                                           <span
                                                className="px-2 py-0.5 text-xs font-medium rounded-full"
                                                style={{ backgroundColor: `${department.color}20`, color: department.color }}
                                            >
                                                {department.name}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 pt-1">
                                        <Icons.Mail className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                        <span className="truncate">{staff.email}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Icons.Phone className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                        <span>{staff.phone}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 dark:bg-slate-700/50 px-5 py-3 flex justify-end items-center gap-2">
                                <button onClick={() => handleViewProfile(staff)} className="flex items-center gap-1.5 px-3 py-1 text-sm text-gray-600 dark:text-gray-200 bg-white dark:bg-slate-600 border border-gray-300 dark:border-slate-500 rounded-md hover:bg-gray-100 dark:hover:bg-slate-500 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500">
                                    <Icons.Eye className="w-4 h-4"/> View
                                </button>
                                <button onClick={() => handleEditStaff(staff)} className="flex items-center gap-1.5 px-3 py-1 text-sm text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 border border-transparent rounded-md hover:bg-indigo-100 dark:hover:bg-indigo-900/40 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500">
                                    <Icons.Edit className="w-4 h-4"/> Edit
                                </button>
                                <button onClick={() => handleDeleteClick(staff)} className="flex items-center gap-1.5 px-3 py-1 text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-transparent rounded-md hover:bg-red-100 dark:hover:bg-red-900/40 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-500">
                                    <Icons.Trash2 className="w-4 h-4"/> Delete
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {isModalOpen && (
                <StaffModal
                    staff={editingStaff}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSaveStaff}
                    departments={departments}
                    weekStartDay={weekStartDay}
                />
            )}
            
            {viewingStaff && (
                <StaffProfileModal
                    staff={viewingStaff}
                    department={viewingStaff.departmentId ? departmentMap.get(viewingStaff.departmentId) : undefined}
                    onClose={() => setViewingStaff(null)}
                />
            )}

            {staffToDelete && (
                <ConfirmationModal
                    title="Delete Staff Member"
                    message={`Are you sure you want to delete ${staffToDelete.name}? This action cannot be undone and will remove them from the schedule.`}
                    confirmButtonText="Delete"
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                    isConfirming={isDeleting}
                />
            )}
        </div>
    );
};

export default StaffManagement;
